import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { TablerIconComponent } from "angular-tabler-icons";
import { BaseComponent } from "src/app/core/base/base.component";
import { MenuServiceParameterizationResponse } from "src/app/core/models/menu-service.model";
import { OrderCheckStorage } from "src/app/core/models/order-check.model";
import { HubService } from "src/app/core/services/hub.service";
import { LoadingService } from "src/app/core/services/loading.service";
import { NotificationService } from "src/app/core/services/notification.service";
import { OrderCheckService } from "src/app/core/services/order-check.service";
import { normalizeAccentedChars } from "src/app/core/utils/functions";
import { TextareaComponent } from "../../inputs/textarea/textarea.component";

@Component({
  selector: "app-order-check-observation",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TextareaComponent,
    TablerIconComponent,
  ],
  templateUrl: "./ordercheck-observation.component.html",
})
export class OrderCheckObservationBottomSheet
  extends BaseComponent
  implements OnInit
{
  // inject
  private notificationService = inject(NotificationService);
  private loadingService = inject(LoadingService);
  private orderCheckService = inject(OrderCheckService);
  private hubService = inject(HubService);
  private _bottomSheetRef =
    inject<MatBottomSheetRef<OrderCheckObservationBottomSheet>>(
      MatBottomSheetRef,
    );

  // variables
  public parameterization: MenuServiceParameterizationResponse =
    this.orderCheckService.getParameterization();
  public orderCheck: OrderCheckStorage =
    this.orderCheckService.getOrderCheckStorage();
  private awaitingHubResponse = false;

  // Form
  form = new FormGroup({
    Observation: new FormControl(this.orderCheck.Observation),
  });

  //#region Getters
  get f() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.onSubscribe();
  }

  onSubscribe() {
    this.subs.push(
      this.hubService.$menuServiceUpdateStatus.subscribe((message: string) => {
        if (!this.awaitingHubResponse) {
          return;
        }
        this.finishHubOperation();
        this.notificationService.showSuccessMessage(
          message || "Observação alterada com sucesso",
        );
        this.orderCheckService.setOrderCheckObservation(
          this.f["Observation"].value,
        );
        this.close();
      }),
    );

    this.subs.push(
      this.hubService.$menuServiceInternalError.subscribe(() => {
        if (!this.awaitingHubResponse) {
          return;
        }
        this.finishHubOperation();
        this.loadingService.hide();
        this.showAlert("Houve um erro ao salvar a observação");
      }),
    );

    this.subs.push(
      this.hubService.$menuServiceClosedTable.subscribe(
        (description: string) => {
          if (!this.awaitingHubResponse) {
            return;
          }
          this.finishHubOperation();
          this.loadingService.hide();
          this.showAlert(description || "Mesa fechada");
        },
      ),
    );

    this.subs.push(
      this.hubService.$menuServiceInvalidTable.subscribe(
        (description: string) => {
          if (!this.awaitingHubResponse) {
            return;
          }
          this.finishHubOperation();
          this.loadingService.hide();
          this.showAlert(description || "Houve um erro ao salvar a observação");
        },
      ),
    );
  }

  close() {
    this.finishHubOperation();
    this._bottomSheetRef.dismiss();
  }

  handleAddObservation() {
    const observation = this.f["Observation"].value?.trim() || "";

    if (observation !== this.orderCheck.Observation) {
      this.beginHubOperation();
      const table = this.orderCheckService.getHubServiceOrderCheckStatus(
        this.parameterization,
        this.orderCheck,
      );
      table.Observation = normalizeAccentedChars(observation);
      this.hubService.updateOrderCheckStatus(table);
      return;
    }

    this.close();
  }

  private beginHubOperation() {
    if (!this.awaitingHubResponse) {
      this.awaitingHubResponse = true;
      this.hubService.beginLocalOperation();
    }
  }

  private finishHubOperation() {
    if (this.awaitingHubResponse) {
      this.awaitingHubResponse = false;
      this.hubService.endLocalOperation();
    }
  }

  private showAlert(message: string) {
    this.notificationService.showAlertMessage(message);
  }

  override ngOnDestroy(): void {
    this.finishHubOperation();
    super.ngOnDestroy();
  }
}
